import clsx from "clsx";
import { type InputHTMLAttributes, forwardRef } from "react";

function renderLabel(label: string) {
  if (label.endsWith(" *")) {
    return (
      <>
        {label.slice(0, -2)}
        <span className="text-rose-500"> *</span>
      </>
    );
  }
  return label;
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  icon?: React.ComponentType<{ className?: string }>;
  multiline?: boolean;
  rows?: number;
  reserveMessageSpace?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      error,
      helperText,
      icon: Icon,
      multiline,
      rows = 3,
      reserveMessageSpace = false,
      ...props
    },
    ref,
  ) => {
    return (
      <div className="space-y-1">
        {label && <label className="form-label block">{renderLabel(label)}</label>}
        <div className="relative">
          {Icon && !multiline && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon className="h-5 w-5 text-slate-400" />
            </div>
          )}
          {multiline ? (
            <textarea
              ref={ref as React.RefObject<any>}
              className={clsx(
                "block w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:placeholder-slate-500 dark:focus:border-blue-400 dark:focus:ring-blue-500/10 resize-none",
                error && "border-red-500 focus:border-red-500 focus:ring-red-500",
                className,
              )}
              rows={rows}
              {...(props as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
            />
          ) : (
            <input
              ref={ref}
              className={clsx(
                "ui-input block w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:placeholder-slate-500 dark:focus:border-blue-400 dark:focus:ring-blue-500/10",
                Icon && "pl-10",
                error && "border-red-500 focus:border-red-500 focus:ring-red-500",
                className,
              )}
              {...props}
            />
          )}
        </div>
        {(error || helperText || reserveMessageSpace) && (
          <p
            className={clsx(
              "min-h-[1.25rem] text-sm",
              error
                ? "text-red-600 dark:text-red-400"
                : helperText
                  ? "text-slate-500 dark:text-slate-400"
                  : "text-transparent",
            )}
          >
            {error ?? helperText ?? " "}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";
